import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { CheckCircle2, AlertTriangle, Info, X, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";

type ToastVariant = "success" | "error" | "warning" | "info";

interface Toast {
  id: string;
  title: string;
  description?: string;
  variant: ToastVariant;
}

interface ToastContextValue {
  toast: (opts: Omit<Toast, "id">) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const variantConfig: Record<
  ToastVariant,
  { icon: typeof CheckCircle2; border: string; iconColor: string }
> = {
  success: {
    icon: CheckCircle2,
    border: "border-l-green-approved",
    iconColor: "text-green-approved",
  },
  error: {
    icon: XCircle,
    border: "border-l-red-danger",
    iconColor: "text-red-danger",
  },
  warning: {
    icon: AlertTriangle,
    border: "border-l-amber-warning",
    iconColor: "text-amber-warning",
  },
  info: {
    icon: Info,
    border: "border-l-navy-blueprint",
    iconColor: "text-navy-blueprint",
  },
};

const TOAST_DURATION_MS = 5000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (opts: Omit<Toast, "id">) => {
      const id = crypto.randomUUID();
      setToasts((prev) => [...prev, { ...opts, id }]);
      window.setTimeout(() => { dismiss(id); }, TOAST_DURATION_MS);
    },
    [dismiss],
  );

  const value = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="pointer-events-none fixed bottom-20 right-4 z-[100] flex flex-col gap-2 md:bottom-4"
        aria-live="polite"
      >
        {toasts.map((t) => {
          const config = variantConfig[t.variant];
          const Icon = config.icon;
          return (
            <div
              key={t.id}
              className={cn(
                "pointer-events-auto flex w-80 items-start gap-3 rounded-lg border border-l-4 bg-card-white p-4 shadow-[var(--shadow-elevated)]",
                config.border,
              )}
            >
              <Icon className={cn("mt-0.5 h-5 w-5 shrink-0", config.iconColor)} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-navy-deep">{t.title}</p>
                {t.description ? (
                  <p className="mt-0.5 text-xs text-muted-foreground">{t.description}</p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => { dismiss(t.id); }}
                className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return ctx;
}
